import { useCallback, useMemo, useRef, useState } from 'react';
import { fetchMaintenanceTable, type MaintenanceTableItem } from '../../../api/fetchMaintenanceTable';
import type { Asset } from '../../../models/asset';
import { type MonitoringRow, mergeTableItem, toSummaryRow } from '../monitoringRow';

const CHUNK_SIZE = 500;
const CONCURRENCY = 4;

export interface BulkProgress {
    done: number;
    total: number;
}

interface ILoadOptions {
    onProgress?: (progress: BulkProgress) => void;
    isCancelled?: () => boolean;
}

const useMonitoringRows = (allAssets: Asset[], onError?: (error: unknown) => void) => {
    const rowsRef = useRef(new Map<string, MonitoringRow>());
    const failedRef = useRef(new Set<string>());
    const inflight = useRef(new Map<string, Promise<void>>());
    const generation = useRef(0);
    const onErrorRef = useRef(onError);
    const [version, setVersion] = useState(0);

    onErrorRef.current = onError;

    const assetMap = useMemo(() => new Map(allAssets.map(asset => [asset.id, asset])), [allAssets]);
    const assetMapRef = useRef(assetMap);
    assetMapRef.current = assetMap;

    const getAsset = useCallback((id: string) => assetMap.get(id), [assetMap]);

    const getRow = useCallback(
        (id: string): MonitoringRow => {
            const cached = rowsRef.current.get(id);
            if (cached) {
                return cached;
            }
            const asset = assetMap.get(id);
            return toSummaryRow(asset ?? ({ id, chassis: '' } as Asset));
        },
        [version, assetMap]
    );

    const isLoaded = useCallback((id: string) => !!rowsRef.current.get(id)?.loaded, [version]);

    const setRow = useCallback((id: string, row: MonitoringRow) => {
        rowsRef.current.set(id, row);
        if (row.failed) {
            failedRef.current.add(id);
        } else {
            failedRef.current.delete(id);
        }
    }, []);

    const loadChunk = useCallback(
        async (ids: string[]) => {
            const startedAt = generation.current;
            const assets = assetMapRef.current;
            let items: MaintenanceTableItem[] = [];
            try {
                items = await fetchMaintenanceTable(ids);
            } catch (error) {
                if (startedAt !== generation.current) {
                    return;
                }
                onErrorRef.current?.(error);
                for (const id of ids) {
                    const asset = assets.get(id);
                    if (asset) {
                        setRow(id, { ...toSummaryRow(asset), loaded: true, failed: true });
                    }
                }
                setVersion(value => value + 1);
                return;
            }
            if (startedAt !== generation.current) {
                return;
            }
            const idByChassis = new Map<string, string>();
            for (const id of ids) {
                const chassis = assets.get(id)?.chassis;
                if (chassis) {
                    idByChassis.set(chassis.trim().toUpperCase(), id);
                }
            }
            const requested = new Set(ids);
            const matched = new Set<string>();
            for (const item of items) {
                const id =
                    [item.id, item.assetId].find(candidate => candidate && requested.has(candidate)) ??
                    idByChassis.get((item.chassis ?? '').trim().toUpperCase());
                if (!id) {
                    continue;
                }
                const asset = assets.get(id);
                const base = toSummaryRow(asset ?? ({ id, chassis: item.chassis ?? '' } as Asset));
                setRow(id, mergeTableItem(base, item));
                matched.add(id);
            }
            for (const id of ids) {
                if (!matched.has(id)) {
                    const asset = assets.get(id);
                    if (asset) {
                        setRow(id, { ...toSummaryRow(asset), loaded: true });
                    }
                }
            }
            setVersion(value => value + 1);
        },
        [setRow]
    );

    const load = useCallback(
        async (ids: string[], { onProgress, isCancelled }: ILoadOptions = {}) => {
            const startedAt = generation.current;
            const stopped = () => startedAt !== generation.current || !!isCancelled?.();
            const pending = ids.filter(id => !rowsRef.current.get(id)?.loaded);
            const total = pending.length;
            let done = 0;
            const advance = (count: number) => {
                done += count;
                if (!stopped()) {
                    onProgress?.({ done: Math.min(done, total), total });
                }
            };
            onProgress?.({ done, total });
            if (!total) {
                return;
            }

            const waitingCount = new Map<Promise<void>, number>();
            const toFetch: string[] = [];
            for (const id of pending) {
                const running = inflight.current.get(id);
                if (running) {
                    waitingCount.set(running, (waitingCount.get(running) ?? 0) + 1);
                } else {
                    toFetch.push(id);
                }
            }

            const chunks: string[][] = [];
            for (let i = 0; i < toFetch.length; i += CHUNK_SIZE) {
                chunks.push(toFetch.slice(i, i + CHUNK_SIZE));
            }

            const runChunk = (chunk: string[]) => {
                const promise = loadChunk(chunk).finally(() => {
                    for (const id of chunk) {
                        if (inflight.current.get(id) === promise) {
                            inflight.current.delete(id);
                        }
                    }
                    advance(chunk.length);
                });
                for (const id of chunk) {
                    inflight.current.set(id, promise);
                }
                return promise;
            };

            const worker = async () => {
                while (chunks.length && !stopped()) {
                    const chunk = chunks.shift();
                    if (chunk) {
                        await runChunk(chunk);
                    }
                }
            };

            const waiting = [...waitingCount].map(([promise, count]) => promise.then(() => advance(count)));
            await Promise.all([...Array.from({ length: CONCURRENCY }, worker), ...waiting]);
            if (!stopped()) {
                onProgress?.({ done: total, total });
            }
        },
        [loadChunk]
    );

    const reload = useCallback(
        async (ids: string[]) => {
            for (const id of ids) {
                const row = rowsRef.current.get(id);
                if (row) {
                    setRow(id, { ...row, loaded: false, failed: false });
                }
            }
            await load(ids);
        },
        [load, setRow]
    );

    const failedIds = useMemo(() => [...failedRef.current], [version]);

    const retryFailed = useCallback(() => reload(failedIds), [reload, failedIds]);

    const clear = useCallback(() => {
        generation.current += 1;
        rowsRef.current.clear();
        failedRef.current.clear();
        inflight.current.clear();
        setVersion(value => value + 1);
    }, []);

    return { getRow, getAsset, isLoaded, load, reload, clear, failedIds, retryFailed };
};

export default useMonitoringRows;
